import React, { useEffect, useRef, useState } from "react";
import "./form.scss";

const ExpenseForm = ({
  data = {},
  feeTypes = [],
  paymentMethods = [],
  resetSelectedActivity = () => {},
  handleExpenseUpdate = () => {},
}) => {
  const [expenseData, setExpenseData] = useState(() => ({
    ...data,
    fee_type_id: data.fee_type_id ?? "",
    payment_method_id: data.payment_method_id ?? "",
  }));
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const editedFieldsRef = useRef(new Set());

  useEffect(() => {
    const expenseType = feeTypes.find(
      (item) => item.name === data.fee_type
    );
    const paymentMethod = paymentMethods.find(
      (item) => item.name === data.payment_method
    );

    setExpenseData((previous) => ({
      ...previous,
      fee_type_id:
        editedFieldsRef.current.has("fee_type_id")
          ? previous.fee_type_id
          : previous.fee_type_id || expenseType?.id || "",
      payment_method_id:
        editedFieldsRef.current.has("payment_method_id")
          ? previous.payment_method_id
          : previous.payment_method_id || paymentMethod?.id || "",
    }));
  }, [data.fee_type, data.payment_method, feeTypes, paymentMethods]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    editedFieldsRef.current.add(name);

    setExpenseData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (savingRef.current) return;

    if (
      !expenseData.fee_type_id ||
      !expenseData.payment_method_id ||
      !Number.isFinite(Number(expenseData.cost)) ||
      Number(expenseData.cost) <= 0
    ) {
      return;
    }

    savingRef.current = true;
    setIsSaving(true);

    try {
      // The parent closes this editor only after a successful API update.
      await handleExpenseUpdate(expenseData);
    } catch (error) {
      console.error("Unable to update expense:", error);
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <div className="ds-expense-edit">
      <form onSubmit={handleFormSubmit}>
        <section className="ds-expense-edit__card">
          <div className="ds-expense-edit__header">
            <span className="ds-expense-edit__eyebrow">
              WORK ORDER EXPENSE
            </span>
            <h2>Edit Expense</h2>
            <p>Update the expense details below.</p>
          </div>

          <div className="ds-expense-edit__field">
            <label htmlFor="ds-edit-expense-type">
              Expense Type
            </label>
            <select
              id="ds-edit-expense-type"
              name="fee_type_id"
              value={expenseData.fee_type_id ?? ""}
              onChange={handleChange}
              disabled={isSaving}
              required
            >
              <option value="">Select expense type</option>
              {feeTypes.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          <div className="ds-expense-edit__field">
            <label htmlFor="ds-edit-expense-cost">
              Cost
            </label>
            <div className="ds-expense-edit__currency">
              <span className="ds-expense-edit__dollar" aria-hidden="true">
                $
              </span>
              <input
                id="ds-edit-expense-cost"
                type="number"
                name="cost"
                inputMode="decimal"
                min="0.01"
                step="0.01"
                value={expenseData.cost ?? ""}
                onChange={handleChange}
                disabled={isSaving}
                required
              />
            </div>
          </div>

          <div className="ds-expense-edit__field">
            <label htmlFor="ds-edit-payment-method">
              Payment Method
            </label>
            <select
              id="ds-edit-payment-method"
              name="payment_method_id"
              value={expenseData.payment_method_id ?? ""}
              onChange={handleChange}
              disabled={isSaving}
              required
            >
              <option value="">Select payment method</option>
              {paymentMethods.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
        </section>

        <div className="ds-expense-edit__actions">
          <button
            type="submit"
            className="ds-expense-edit__save"
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Expense"}
          </button>

          <button
            type="button"
            className="ds-expense-edit__cancel"
            onClick={resetSelectedActivity}
            disabled={isSaving}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ExpenseForm;
